// Evidence chain (PRD §4.4, US-25, US-03): trial or document, result, reviewer, approval date, in one place. A
// lab-report PDF is stored in the private evidence bucket with its sha256; the values it supports are entered
// manually and linked. Review and approval are stamped by the database and refused for the submitter. Links are
// chosen from lists (batch, rule, value of a batch) and shown by name; a search narrows the list (?q= in links).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { PageHeader } from '../components/Layout.tsx';
import { Enum, ErrorBox, fmtValue, Loading } from '../components/ui.tsx';
import { batchChain } from '../lib/chain.ts';
import { activePack, evidence, myRoles, productBatches, profiles } from '../lib/data.ts';
import { operatorClient } from '../lib/supabase.ts';
import { fieldDef, type FieldDef } from '../lib/template.ts';
import type { NodeRow, ValueRow } from '../lib/types.ts';

const KINDS = ['lab_report', 'trial', 'coa', 'study', 'legal_opinion', 'sop'];
const LINK_KINDS = ['batch_id', 'rule_id', 'value_id'] as const;
type LinkKind = (typeof LINK_KINDS)[number];

async function sha256(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

interface Draft {
  evidence_id: string;
  kind: string;
  title: string;
  result_summary: string;
  link_kind: LinkKind;
  link_batch: string;
  link_rule: string;
  link_value: string;
}

const EMPTY: Draft = {
  evidence_id: '',
  kind: 'lab_report',
  title: '',
  result_summary: '',
  link_kind: 'batch_id',
  link_batch: '',
  link_rule: '',
  link_value: '',
};

interface RuleDef {
  rule_id: string;
  criterion: string;
  evidence_required?: string;
  overlay?: string;
}

interface EvidenceLink {
  value_id: number | null;
  rule_id: string | null;
  batch_id: string | null;
}

type LinkedValue = Pick<ValueRow, 'value_id' | 'field_id' | 'node_id' | 'value' | 'unit' | 'version'>;

export function Evidence() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Record<string, unknown>[] | null>(null);
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const [roles, setRoles] = useState<string[]>([]);
  const [me, setMe] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [params, setParams] = useSearchParams();
  const [batches, setBatches] = useState<NodeRow[]>([]);
  const [rules, setRules] = useState<RuleDef[]>([]);
  const [fields, setFields] = useState<FieldDef[]>([]);
  const [linked, setLinked] = useState<Map<number, LinkedValue>>(new Map());
  const [choices, setChoices] = useState<LinkedValue[]>([]);
  const q = params.get('q') ?? '';

  const load = useCallback(async () => {
    try {
      const list = await evidence();
      setRows(list);
      setNames(await profiles());
      setRoles(await myRoles());
      setMe((await operatorClient().auth.getUser()).data.user?.id ?? null);
      // the values that evidence points to, by id, whatever their version
      const ids = list.flatMap((e) =>
        ((e['evidence_links'] as EvidenceLink[] | null) ?? []).flatMap((l) =>
          l.value_id ? [l.value_id] : [],
        ),
      );
      if (ids.length > 0) {
        const { data } = await operatorClient()
          .from('field_values')
          .select('value_id, field_id, node_id, value, unit, version')
          .in('value_id', ids);
        setLinked(new Map(((data ?? []) as LinkedValue[]).map((v) => [v.value_id, v])));
      }
    } catch (e) {
      setError(e);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void (async () => {
      try {
        const [pack, list] = await Promise.all([activePack(), productBatches()]);
        const p = pack?.pack as unknown as { fields: { items: FieldDef[] }; rules: { items: RuleDef[] } };
        setFields(p?.fields.items ?? []);
        setRules((p?.rules.items ?? []).filter((r) => !(r.overlay ?? '').startsWith('retired')));
        setBatches(list);
      } catch (e) {
        setError(e);
      }
    })();
  }, []);

  // the values of the chosen batch's chain, to link a lab report to the values it supports
  useEffect(() => {
    if (draft.link_kind !== 'value_id' || !draft.link_batch) {
      setChoices([]);
      return;
    }
    void (async () => {
      const { steps, prodCode } = await batchChain(draft.link_batch);
      const ids = [
        ...steps.map((x) => x.id).filter((x): x is string => !!x),
        ...(prodCode ? [prodCode] : []),
      ];
      const { data } = await operatorClient()
        .from('current_values')
        .select('value_id, field_id, node_id, value, unit, version')
        .in('node_id', ids)
        .not('value', 'is', null)
        .order('field_id');
      setChoices((data ?? []) as LinkedValue[]);
    })();
  }, [draft.link_kind, draft.link_batch]);

  const fieldName = useCallback(
    (fid: string) => {
      const def = fieldDef(fields, fid);
      if (!def) return fid;
      return def.field_id === fid ? def.field_name : `${def.field_name}: ${fid.split(/[._]/).pop() ?? ''}`;
    },
    [fields],
  );
  const criterion = useCallback(
    (rid: string) => rules.find((r) => r.rule_id === rid)?.criterion ?? '',
    [rules],
  );
  const describe = useCallback(
    (v: LinkedValue) =>
      `${fieldName(v.field_id)} (${v.node_id}): ${fmtValue(v.value)}${v.unit ? ` ${v.unit}` : ''}`,
    [fieldName],
  );

  // the search matches the evidence id, title, result, and what it supports, by code or by name
  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle || !rows) return rows ?? [];
    return rows.filter((e) => {
      const ls = (e['evidence_links'] as EvidenceLink[] | null) ?? [];
      const hay = [
        e['evidence_id'],
        e['title'],
        e['result_summary'],
        ...ls.flatMap((l) => {
          const v = l.value_id ? linked.get(l.value_id) : undefined;
          return [
            l.batch_id,
            l.rule_id,
            l.rule_id ? criterion(l.rule_id) : '',
            v?.field_id,
            v?.node_id,
            v && describe(v),
          ];
        }),
      ]
        .map((x) => String(x ?? ''))
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, q, linked, criterion, describe]);

  async function update(rowId: unknown, patch: Record<string, unknown>) {
    setError(null);
    setNotice('');
    const { error: err } = await operatorClient().from('evidence').update(patch).eq('evidence_row_id', rowId);
    if (err) setError(new Error(err.message));
    await load();
  }

  async function submit(ev: React.FormEvent) {
    ev.preventDefault();
    setBusy(true);
    setError(null);
    setNotice('');
    try {
      const sb = operatorClient();
      let document_ref: string | null = null;
      let document_sha256: string | null = null;
      if (file) {
        if (!me) throw new Error(t('common.not_signed_in'));
        document_sha256 = await sha256(file);
        document_ref = `${me}/${draft.evidence_id}/${document_sha256}.pdf`;
        const up = await sb.storage
          .from('evidence')
          .upload(document_ref, file, { contentType: 'application/pdf', upsert: false });
        if (up.error) throw new Error(up.error.message);
      }
      const { data, error: err } = await sb
        .from('evidence')
        .insert({
          evidence_id: draft.evidence_id,
          kind: draft.kind,
          title: draft.title,
          result_summary: draft.result_summary || null,
          document_ref,
          document_sha256,
          status: 'under_review',
        })
        .select('evidence_row_id, version')
        .single();
      if (err) throw new Error(err.message);
      const row = data as { evidence_row_id: number; version: number };
      const target =
        draft.link_kind === 'batch_id'
          ? draft.link_batch
          : draft.link_kind === 'rule_id'
            ? draft.link_rule
            : draft.link_value
              ? Number(draft.link_value)
              : '';
      if (target !== '') {
        const l = await sb
          .from('evidence_links')
          .insert({ evidence_row_id: row.evidence_row_id, [draft.link_kind]: target });
        if (l.error) throw new Error(l.error.message);
      }
      setNotice(t('evidence.saved', { id: draft.evidence_id, version: row.version }));
      setDraft(EMPTY);
      setFile(null);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
      await load();
    }
  }

  async function open(path: string, expected: string | null) {
    setError(null);
    setNotice('');
    const { data, error: err } = await operatorClient().storage.from('evidence').download(path);
    if (err || !data) {
      setError(new Error(err?.message ?? t('evidence.download_failed')));
      return;
    }
    const actual = await sha256(data);
    if (expected && actual !== expected) {
      setError(new Error(t('evidence.hash_mismatch', { expected, actual })));
      return;
    }
    setNotice(t('evidence.hash_ok', { hash: actual }));
    const a = document.createElement('a');
    a.href = URL.createObjectURL(data);
    a.download = path.split('/').pop() ?? 'evidence.pdf';
    a.click();
    URL.revokeObjectURL(a.href);
  }

  if (!rows) return <Loading />;
  const reviewer = roles.some((r) => ['quality_manager', 'regulatory_owner', 'platform_admin'].includes(r));
  const submitter = reviewer || roles.includes('data_provider');
  const who = (id: unknown) => (typeof id === 'string' ? (names.get(id) ?? id.slice(0, 8)) : '—');
  const when = (ts: unknown) => (typeof ts === 'string' ? ts.slice(0, 16).replace('T', ' ') : '');
  const text = (k: keyof Draft, label: string) => (
    <label>
      {label} <input value={draft[k]} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} />
    </label>
  );

  return (
    <main>
      <PageHeader title={t('evidence.title')} page="evidence" />
      <ErrorBox error={error} />
      {notice && <p className="ok">{notice}</p>}
      {submitter && (
        <details>
          <summary>{t('evidence.new')}</summary>
          <form className="form" onSubmit={(e) => void submit(e)}>
            {text('evidence_id', t('evidence.id'))}
            <label>
              {t('evidence.kind')}{' '}
              <select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value })}>
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`evidence_kind.${k}`, k)}
                  </option>
                ))}
              </select>
            </label>
            {text('title', t('evidence.doc_title'))}
            {text('result_summary', t('evidence.result'))}
            <label>
              {t('evidence.link')}
              <select
                value={draft.link_kind}
                onChange={(e) => setDraft({ ...draft, link_kind: e.target.value as LinkKind })}
              >
                {LINK_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`evidence.link_kind.${k}`)}
                  </option>
                ))}
              </select>
            </label>
            {draft.link_kind !== 'rule_id' && (
              <label>
                {t('col.batch')}
                <select
                  value={draft.link_batch}
                  onChange={(e) => setDraft({ ...draft, link_batch: e.target.value, link_value: '' })}
                >
                  <option value="">{t('evidence.choose')}</option>
                  {batches.map((b) => (
                    <option key={b.node_id} value={b.node_id}>
                      {b.node_id} · {b.prod_code}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {draft.link_kind === 'rule_id' && (
              <label>
                {t('evidence.rule')}
                <select
                  value={draft.link_rule}
                  onChange={(e) => setDraft({ ...draft, link_rule: e.target.value })}
                >
                  <option value="">{t('evidence.choose')}</option>
                  {rules
                    .filter((r) => r.evidence_required && r.evidence_required !== '—')
                    .map((r) => (
                      <option key={r.rule_id} value={r.rule_id}>
                        {r.rule_id} · {r.criterion}
                      </option>
                    ))}
                </select>
              </label>
            )}
            {draft.link_kind === 'value_id' && (
              <label>
                {t('evidence.value')}
                <select
                  value={draft.link_value}
                  disabled={!draft.link_batch}
                  onChange={(e) => setDraft({ ...draft, link_value: e.target.value })}
                >
                  <option value="">{t('evidence.choose')}</option>
                  {choices.map((v) => (
                    <option key={v.value_id} value={v.value_id}>
                      {describe(v)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              {t('evidence.pdf')}{' '}
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </label>
            <button type="submit" disabled={busy || !draft.evidence_id.trim() || !draft.title.trim()}>
              {t('evidence.submit')}
            </button>
          </form>
          <p className="small muted">{t('evidence.new_help')}</p>
        </details>
      )}
      <p className="filters">
        <label>
          {t('evidence.search')}{' '}
          <input
            type="search"
            size={32}
            value={q}
            placeholder={t('evidence.search_hint')}
            onChange={(e) => {
              const next = new URLSearchParams(params);
              if (e.target.value) next.set('q', e.target.value);
              else next.delete('q');
              setParams(next, { replace: true });
            }}
          />
        </label>{' '}
        <span className="muted small">{t('evidence.count', { count: visible.length })}</span>
      </p>
      <table>
        <thead>
          <tr>
            <th>{t('evidence.id')}</th>
            <th>{t('col.version_short')}</th>
            <th>{t('evidence.kind')}</th>
            <th>{t('values.status')}</th>
            <th>{t('evidence.submitted')}</th>
            <th>{t('evidence.reviewed')}</th>
            <th>{t('evidence.approved')}</th>
            <th>{t('evidence.links')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {visible.map((e) => {
            const own = me !== null && e['submitted_by'] === me;
            const status = String(e['status']);
            const docRef = typeof e['document_ref'] === 'string' ? e['document_ref'] : null;
            return (
              <tr key={String(e['evidence_row_id'])}>
                <td>
                  <strong>{String(e['evidence_id'])}</strong>
                  <div className="small muted">
                    {String(e['title'])}
                    {e['result_summary'] ? ` · ${String(e['result_summary'])}` : ''}
                  </div>
                  {docRef && (
                    <button
                      onClick={() =>
                        void open(
                          docRef,
                          typeof e['document_sha256'] === 'string' ? e['document_sha256'] : null,
                        )
                      }
                    >
                      {t('evidence.open')}
                    </button>
                  )}
                </td>
                <td>{String(e['version'])}</td>
                <td>
                  <Enum ns="evidence_kind" v={String(e['kind'])} />
                </td>
                <td>
                  <Enum ns="evidence_status" v={status} />
                </td>
                <td>
                  {who(e['submitted_by'])}
                  {own && <strong> ({t('values.you')})</strong>}{' '}
                  <span className="small muted">{when(e['submitted_at'])}</span>
                </td>
                <td>
                  {who(e['reviewed_by'])} <span className="small muted">{when(e['reviewed_at'])}</span>
                </td>
                <td>
                  {who(e['approved_by'])} <span className="small muted">{when(e['approved_at'])}</span>
                </td>
                <td className="small">
                  {((e['evidence_links'] as EvidenceLink[] | null) ?? []).map((l, i) => {
                    const v = l.value_id ? linked.get(l.value_id) : undefined;
                    return (
                      <div key={i}>
                        {l.batch_id && <Link to={`/batches/${l.batch_id}`}>{l.batch_id}</Link>}
                        {l.rule_id && (
                          <>
                            <code>{l.rule_id}</code> {criterion(l.rule_id)}
                          </>
                        )}
                        {l.value_id &&
                          (v ? (
                            <>
                              {describe(v)}{' '}
                              <span className="muted">
                                {t('col.version_short')} {v.version}
                              </span>
                            </>
                          ) : (
                            <span className="muted">{t('evidence.value_hidden')}</span>
                          ))}
                      </div>
                    );
                  })}
                </td>
                <td>
                  {reviewer && status !== 'approved' && status !== 'withdrawn' && (
                    <>
                      {!e['reviewed_by'] && (
                        <button
                          onClick={() =>
                            void update(e['evidence_row_id'], {
                              review_note: 'reviewed in the operator interface',
                            })
                          }
                        >
                          {t('evidence.review')}
                        </button>
                      )}
                      {Boolean(e['reviewed_by']) && (
                        <button onClick={() => void update(e['evidence_row_id'], { status: 'approved' })}>
                          {t('evidence.approve')}
                        </button>
                      )}
                    </>
                  )}
                  {(reviewer || own) && status !== 'withdrawn' && (
                    <button
                      className="secondary"
                      onClick={() => void update(e['evidence_row_id'], { status: 'withdrawn' })}
                    >
                      {t('evidence.withdraw')}
                    </button>
                  )}
                  {own && reviewer && status !== 'approved' && status !== 'withdrawn' && (
                    <div className="small muted">{t('values.sod_hint')}</div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </main>
  );
}
